/*
  Kalmora — motion.js
  Sistema de movimiento ligado al scroll (DS §16). Sin dependencias.

  Atributos que lee:
  - [data-reveal]           Aparece una sola vez al entrar en pantalla (clase .is-in).
  - [data-scroll="exit"]    Escribe --p (0 → 1) mientras la sección sale por arriba (hero).
  - [data-scroll="through"] Escribe --p (0 → 1) mientras la sección cruza la pantalla.
  - [data-scroll="fill"]    Escribe --p (0 → 1) desde que entra hasta que llega al centro.
  - [data-scroll="pin"]     Escribe --p (0 → 1) mientras su escenario fijo (sticky) está en pantalla.
  - [data-chapters]         Marca el capítulo que cruza el centro de la pantalla (data-active).
  - [data-sticky-buy]       Barra de compra: visible tras el hero y oculta al llegar al CTA final.
  - [data-tone]             Escribe --e (0 → 1) mientras la sección entra (su borde superior sube del 100 % al 40 %
                            de la pantalla) y --x (0 → 1) mientras sale (su borde inferior baja del 60 % al 0 %).
                            Sirve para fundir el fondo con el de la sección vecina (claro ↔ oscuro).
  - [data-scroll-progress]  Línea de progreso de la página: se escala en horizontal (transform: scaleX).
  - [data-focus-track]      Carrusel: marca con .is-focus el elemento que ocupa la mayor parte del carrusel.

  Solo escribe una variable CSS por elemento y fotograma; el CSS decide transform y opacity.
  Con prefers-reduced-motion no hay movimiento ligado al scroll y todo se muestra en su estado final.
*/
(() => {
  const root = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const tracked = new Set();
  let stickyBuy = null;
  let progressBar = null;
  let stickyStart = null;
  let stickyEnd = null;
  let frame = 0;

  const clamp = (value) => Math.min(1, Math.max(0, value));

  /* ── Aparición al hacer scroll (una sola vez) ── */
  const revealObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-in');
        revealObserver.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
  );

  /* ── Progreso ligado al scroll: solo se calcula para lo que está en pantalla ── */
  const scrollObserver = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) tracked.add(entry.target);
      else tracked.delete(entry.target);
    }
    requestUpdate();
  });

  /* ── Capítulos: el que cruza la línea central de la pantalla es el activo ── */
  const chapterObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const chapter = entry.target;
        const group = chapter.closest('[data-chapters]');
        if (!group) continue;
        group.dataset.active = chapter.dataset.chapter;
        group.querySelectorAll('[data-chapter]').forEach((item) => {
          item.classList.toggle('is-active', item === chapter);
        });
      }
    },
    { rootMargin: '-48% 0px -48% 0px' }
  );

  function progress(el) {
    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight;
    switch (el.dataset.scroll) {
      case 'exit':
        return clamp(-rect.top / Math.max(rect.height, 1));
      case 'fill':
        return clamp((vh * 0.9 - rect.top) / (vh * 0.4 + rect.height * 0.6));
      case 'pin':
        return clamp(-rect.top / Math.max(rect.height - vh, 1));
      default:
        return clamp((vh - rect.top) / (vh + rect.height));
    }
  }

  function update() {
    frame = 0;

    if (!reduceMotion.matches) {
      const vh = window.innerHeight;
      tracked.forEach((el) => {
        if (el.dataset.scroll) el.style.setProperty('--p', progress(el).toFixed(4));
        if (el.hasAttribute('data-tone')) {
          const rect = el.getBoundingClientRect();
          el.style.setProperty('--e', clamp((vh - rect.top) / (vh * 0.6)).toFixed(4));
          el.style.setProperty('--x', clamp((vh * 0.6 - rect.bottom) / (vh * 0.6)).toFixed(4));
        }
      });
    }

    if (progressBar) {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      progressBar.style.transform = `scaleX(${max > 0 ? clamp(window.scrollY / max).toFixed(4) : 0})`;
    }

    root.toggleAttribute('data-scrolled', window.scrollY > 8);

    if (stickyBuy) {
      const vh = window.innerHeight;
      const passedStart = stickyStart ? stickyStart.getBoundingClientRect().bottom < 0 : window.scrollY > vh;
      const reachedEnd = stickyEnd ? stickyEnd.getBoundingClientRect().top < vh : false;
      const visible = passedStart && !reachedEnd;
      if (visible !== stickyBuy.hasAttribute('data-visible')) {
        stickyBuy.toggleAttribute('data-visible', visible);
        stickyBuy.inert = !visible;
      }
    }
  }

  /* ── Carrusel: el elemento más visible gana .is-focus (sigue al dedo, sin cálculos por fotograma) ── */
  function initFocusTrack(track) {
    if (track.dataset.focusReady) return;
    track.dataset.focusReady = 'true';
    const items = Array.from(track.children);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.intersectionRatio >= 0.6) {
            items.forEach((item) => item.classList.toggle('is-focus', item === entry.target));
          }
        });
      },
      { root: track, threshold: [0.6] }
    );
    items.forEach((item) => observer.observe(item));
    if (items[0]) items[0].classList.add('is-focus');
  }

  function requestUpdate() {
    if (!frame) frame = window.requestAnimationFrame(update);
  }

  function init(scope) {
    scope.querySelectorAll('[data-reveal]').forEach((el) => revealObserver.observe(el));
    scope.querySelectorAll('[data-scroll], [data-tone]').forEach((el) => scrollObserver.observe(el));
    scope.querySelectorAll('[data-focus-track]').forEach(initFocusTrack);
    scope.querySelectorAll('[data-chapters] [data-chapter]').forEach((el) => chapterObserver.observe(el));

    progressBar = document.querySelector('[data-scroll-progress]');
    stickyBuy = document.querySelector('[data-sticky-buy]');
    stickyStart = document.querySelector('[data-sticky-buy-start]');
    stickyEnd = document.querySelector('[data-sticky-buy-end]');
    requestUpdate();
  }

  window.addEventListener('scroll', requestUpdate, { passive: true });
  window.addEventListener('resize', requestUpdate, { passive: true });
  reduceMotion.addEventListener('change', () => {
    tracked.forEach((el) => {
      el.style.removeProperty('--p');
      el.style.removeProperty('--e');
      el.style.removeProperty('--x');
    });
    requestUpdate();
  });

  /* Theme Editor: las secciones se vuelven a pintar al editarlas */
  document.addEventListener('shopify:section:load', (event) => init(event.target));

  init(document);
})();
